"use client";

import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HoverGlow, trackHoverGlow } from "./_lib/HoverGlow";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";

gsap.registerPlugin(ScrollTrigger);

interface Testimonial {
  name: string;
  country: string;
  quote: string;
}


const ROW_1: Testimonial[] = [
  {
    name: "Esin B.",
    country: "Turkey",
    quote:
      "\u201CIf you said it will be aligned with the syllabus, 10 out of 10.\u201D",
  },
  {
    name: "Esin B.",
    country: "Turkey",
    quote:
      "\u201CIf the syllabus is really integrated well, it will be more useful to use it in class.\u201D",
  },
  {
    name: "Emre D.",
    country: "Turkey",
    quote:
      "\u201CIf it includes all the things that you say, I can say 10 out of 10.\u201D",
  },
  {
    name: "Aaron H.",
    country: "Indonesia",
    quote:
      "\u201CThis is way more interactive and way more fun. Kids would consider this more of a game compared to a lesson.\u201D",
  },
  {
    name: "Anisha",
    country: "Indonesia",
    quote: "\u201COur students love to compete.\u201D",
  },
  {
    name: "Anisha",
    country: "Indonesia",
    quote: "\u201CTen out of ten. I can\u2019t wait.\u201D",
  },
  {
    name: "Karina",
    country: "Peru",
    quote:
      "\u201CThat is really encouraging. That will make me feel OK that I need to use the app every day.\u201D",
  },
  {
    name: "Karina",
    country: "Peru",
    quote:
      "\u201CMaybe let\u2019s play a game. So to see how much they know.\u201D",
  },
];

const ROW_2: Testimonial[] = [
  {
    name: "Daniela",
    country: "Argentina",
    quote:
      "\u201CWith this app, it\u2019s done\u2026 you can have the information without marking. And that\u2019s great.\u201D",
  },
  {
    name: "Daniela",
    country: "Argentina",
    quote:
      "\u201CIt\u2019s going to help us to know our students\u2019 progress very, very fast without wasting time.\u201D",
  },
  {
    name: "Solana",
    country: "Argentina",
    quote:
      "\u201CIt\u2019s really useful because sometimes it\u2019s hard to follow the progress of all students.\u201D",
  },
  {
    name: "Karina",
    country: "Peru",
    quote:
      "\u201CI really love the red notes there below each name.\u201D",
  },
  {
    name: "Irene",
    country: "Italy",
    quote:
      "\u201CI would use this much more to keep track of their motivation than of their learning.\u201D",
  },
  {
    name: "Irene",
    country: "Italy",
    quote:
      "\u201CAdult learners are desperately looking for something like this.\u201D",
  },
  {
    name: "Karina",
    country: "Peru",
    quote:
      "\u201CThat is a really, really friendly user\u2026 most of us are older than 30. We\u2019re not so techy.\u201D",
  },
  {
    name: "Anisha",
    country: "Indonesia",
    quote:
      "\u201COh, that\u2019s pretty cool\u2026 it would help our students so much.\u201D",
  },
];

const MOBILE_PICKS: Testimonial[] = [
  ROW_1[3], // Aaron H. - gamification
  ROW_2[0], // Daniela - teacher dashboard
  ROW_1[0], // Esin B. - syllabus alignment
  ROW_2[5], // Irene - pedagogy / learner fit
  ROW_2[3], // Karina - dashboard red notes
];

function TestimonialCard({
  name,
  country,
  quote,
  fluid,
}: Testimonial & { fluid?: boolean }) {
  return (
    <div
      className={`group relative ${fluid ? "w-full" : "w-[340px] md:w-[380px]"} shrink-0`}
      onMouseMove={trackHoverGlow}
    >
      <div className="relative h-full rounded-xl overflow-hidden bg-white/10 border border-white/10 flex flex-col">
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <p className="text-[16px] font-semibold leading-normal text-white tracking-[-0.32px]">
              {name}
            </p>
            <p className="text-[14px] font-normal leading-[14px] text-white/80 tracking-[0.28px]">
              {country}
            </p>
          </div>
        </div>
        <div className="h-px w-full bg-white/10" />
        <div className="p-4 flex-1">
          <p className="text-[16px] font-normal leading-[24px] text-white tracking-[0.32px]">
            {quote}
          </p>
        </div>
      </div>
      <HoverGlow rounded="rounded-xl" outerRounded="rounded-[16px]" />
    </div>
  );
}

function MarqueeRow({
  items,
  trackRef,
}: {
  items: Testimonial[];
  trackRef: React.RefObject<HTMLDivElement | null>;
}) {
  const doubled = [...items, ...items];

  return (
    <div className="overflow-hidden w-full">
      <div ref={trackRef} className="flex gap-3 w-max">
        {doubled.map((t, i) => (
          <TestimonialCard key={`${t.name}-${i}`} {...t} />
        ))}
      </div>
    </div>
  );
}

export default function TeachersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const mobileRowsRef = useRef<HTMLDivElement>(null);
  const desktopRowsRef = useRef<HTMLDivElement>(null);
  const marqueeLeftRef = useRef<HTMLDivElement>(null);
  const marqueeRightRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      if (prefersReducedMotion) {
        if (headingRef.current) gsap.set(headingRef.current, { opacity: 1, y: 0 });
        if (subtitleRef.current) gsap.set(subtitleRef.current, { opacity: 1, y: 0 });
        if (desktopRowsRef.current) gsap.set(desktopRowsRef.current, { opacity: 1, y: 0 });
        if (mobileRowsRef.current) {
          const mobileCards = gsap.utils.toArray<HTMLElement>(
            mobileRowsRef.current.children
          );
          gsap.set(mobileCards, { opacity: 1, scale: 1 });
        }
        return;
      }

      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: headingRef.current,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          }
        );
      }

      if (subtitleRef.current) {
        gsap.fromTo(
          subtitleRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: subtitleRef.current,
              start: "top 88%",
              toggleActions: "play none none none",
            },
          }
        );
      }

      if (desktopRowsRef.current) {
        gsap.fromTo(
          desktopRowsRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: desktopRowsRef.current,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          }
        );
      }

      if (mobileRowsRef.current) {
        const mobileCards = gsap.utils.toArray<HTMLElement>(
          mobileRowsRef.current.children
        );
        gsap.fromTo(
          mobileCards,
          { opacity: 0, scale: 0.96 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.6,
            stagger: 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: mobileRowsRef.current,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          }
        );
      }

      const leftTrack = marqueeLeftRef.current;
      const rightTrack = marqueeRightRef.current;
      const leftTween = leftTrack
        ? gsap.to(leftTrack, {
            xPercent: -50,
            duration: 80,
            ease: "none",
            repeat: -1,
            paused: true,
          })
        : null;
      const rightTween = rightTrack
        ? gsap.fromTo(
            rightTrack,
            { xPercent: -50 },
            {
              xPercent: 0,
              duration: 90,
              ease: "none",
              repeat: -1,
              paused: true,
            }
          )
        : null;

      if (sectionRef.current && (leftTween || rightTween)) {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          onEnter: () => {
            leftTween?.play();
            rightTween?.play();
          },
          onLeave: () => {
            leftTween?.pause();
            rightTween?.pause();
          },
          onEnterBack: () => {
            leftTween?.play();
            rightTween?.play();
          },
          onLeaveBack: () => {
            leftTween?.pause();
            rightTween?.pause();
          },
        });
      }
    }, sectionRef.current);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      id="customers"
      className="relative w-full py-16 md:py-[120px] overflow-hidden"
    >
      <div className="relative z-10 flex flex-col items-center">
        <h2
          ref={headingRef}
          className="text-[32px] md:text-[40px] font-semibold leading-[40px] md:leading-[48px] mb-4 bg-clip-text text-transparent text-center px-5"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #ffffff 0%, #b7a8ea 100%)",
          }}
        >
          What teachers told us
        </h2>

        <p
          ref={subtitleRef}
          className="text-[14px] md:text-[16px] font-normal leading-[22px] md:leading-[24px] text-white/80 text-center max-w-[600px] mb-6 md:mb-10 px-5"
        >
          We&apos;ve conducted over 50 teacher interviews and more than 30 usability
          tests before the pilot release.
        </p>

        {/* Mobile: stacked vertical cards */}
        <div ref={mobileRowsRef} className="flex flex-col gap-3 w-full px-5 md:hidden">
          {MOBILE_PICKS.map((t, i) => (
            <TestimonialCard key={`mobile-${i}`} {...t} fluid />
          ))}
        </div>

        {/* Desktop: marquee rows */}
        <div ref={desktopRowsRef} className="hidden md:flex flex-col gap-3 w-full">
          <MarqueeRow items={ROW_1} trackRef={marqueeLeftRef} />
          <MarqueeRow items={ROW_2} trackRef={marqueeRightRef} />
        </div>
      </div>
    </section>
  );
}
